import {
  CheckIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
} from '@heroicons/react/24/outline';
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import {
  AdConstraint,
  AdGroupConstraint,
  AdsCampaign,
  AdsCampaignConstraint,
  AdsGroup,
  AdsGroupAd,
  Constraints,
  ConstraintType,
  DeviceConstraint,
  FacebookAdsAdSetAd,
  GoogleAdsAdGroupAd,
} from '@superfunnel/types';
import Autocomplete from '@superfunnel/ui-core/Autocomplete';
import ChipsAutocomplete from '@superfunnel/ui-core/ChipsAutocomplete';
import Loader from '@superfunnel/ui-core/Loader';
import Button from '@superfunnel/ui-core/v2/Button';
import Chip from '@superfunnel/ui-core/v2/Chip';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@superfunnel/ui-core/v2/ToggleGroup';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@superfunnel/ui-core/v2/popover';
import { normalizeLocationString } from '@superfunnel/utils';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { uid } from 'uid';
import facebookAdsIcon from '../../assets/images/facebookAdsIcon.png';
import googleAdsIcon from '../../assets/images/googleAdsIcon.png';
import { UserContext } from '../../contexts/UserContext';
import {
  getCachedAdGroupAds,
  getCachedAdGroups,
  getCachedCampaigns,
} from '../../services/AdsAccountsService';
import { capitalize, classNames, distinct } from '../../utils';
import { getConstraintTitle } from '../../utils/constraints.utils';
import Breadcrumbs from '../Breadcrumbs';
import { SearchField } from '../SearchField';
import { AdPreview } from './AdPreview';
import { KeywordsConstraintSelectorModal } from './KeywordsConstraintSelectorModal';

const daysOfWeek = [
  { value: 'Mon', title: 'Monday' },
  { value: 'Tue', title: 'Tuesday' },
  { value: 'Wed', title: 'Wednesday' },
  { value: 'Thu', title: 'Thursday' },
  { value: 'Fri', title: 'Friday' },
  { value: 'Sat', title: 'Saturday' },
  { value: 'Sun', title: 'Sunday' },
];

const hoursOfDay = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  title: `${i < 10 ? '0' : ''}${i}:00`,
}));

interface Props {
  constraints: Constraints;
  onChange: (_: Constraints) => void;
  readOnly?: boolean;
  visibleConstraints?: ConstraintType[];
}

export default function ConstraintsForm({
  constraints,
  onChange,
  readOnly,
  visibleConstraints,
}: Props) {
  const [selectKeywordsModalOpen, setSelectKeywordsModalOpen] = useState(false);
  const [countryCodes, setCountryCodes] = useState<
    { code: string; country: string; flag: string; name: string }[]
  >([]);
  const [timezonesList, setTimezonesList] = useState<string[]>([]);
  const [regionsByCountry, setRegionsByCountry] = useState<
    Record<string, { code: string; name: string }[]>
  >({});
  const [citiesList, setCitiesList] = useState<
    { name: string; country: string; region: string }[]
  >([]);

  const showKeywords =
    !visibleConstraints || visibleConstraints.includes('keywords');

  const showDevices =
    !visibleConstraints || visibleConstraints.includes('devices');

  const showAds = !visibleConstraints || visibleConstraints.includes('ads');

  const showAdsGroup =
    !visibleConstraints || visibleConstraints.includes('adGroups');

  const showCampaigns =
    !visibleConstraints || visibleConstraints.includes('campaigns');

  const showSchedules =
    !visibleConstraints || visibleConstraints.includes('schedules');

  const showCountries =
    !visibleConstraints || visibleConstraints.includes('countries');

  const showRegions =
    !visibleConstraints || visibleConstraints.includes('regions');

  const showCities =
    !visibleConstraints || visibleConstraints.includes('cities');

  // Lazy load countryCodes and timezonesList
  useEffect(() => {
    if (showCountries && countryCodes.length === 0) {
      import('../../assets/phone-country-codes.json').then((module) => {
        setCountryCodes(module.default);
      });
    }
  }, [showCountries, countryCodes.length]);

  useEffect(() => {
    if (showSchedules && timezonesList.length === 0) {
      import('../../assets/timezones.json').then((module) => {
        setTimezonesList(module.default);
      });
    }
  }, [showSchedules, timezonesList.length]);

  // Lazy load regions data
  useEffect(() => {
    if (showRegions && Object.keys(regionsByCountry).length === 0) {
      import('../../assets/regions-by-country.json').then((module) => {
        setRegionsByCountry(module.default);
      });
    }
  }, [showRegions, regionsByCountry]);

  // Lazy load cities data
  useEffect(() => {
    if (showCities && citiesList.length === 0) {
      import('../../assets/cities.json').then((module) => {
        setCitiesList(module.default);
      });
    }
  }, [showCities, citiesList.length]);

  // Get available regions based on selected countries
  const availableRegions = useMemo(() => {
    if (!constraints.countries?.length) {
      // If no countries selected, show all regions
      return Object.entries(regionsByCountry).flatMap(
        ([countryCode, regions]) => regions.map((r) => ({ ...r, countryCode })),
      );
    }
    // Filter regions to only selected countries
    return constraints.countries.flatMap((country) => {
      const regions = regionsByCountry[country.code] || [];
      return regions.map((r) => ({ ...r, countryCode: country.code }));
    });
  }, [constraints.countries, regionsByCountry]);

  // Get available cities based on selected countries
  const availableCities = useMemo(() => {
    const cities = citiesList.map((c) => ({
      id: `${c.country}-${c.region}-${c.name}`,
      name: normalizeLocationString(c.name),
      displayName: `${c.name}, ${c.country}`,
      countryCode: c.country,
      regionCode: c.region,
    }));

    if (!constraints.countries?.length) {
      return cities;
    }
    // Filter cities to only selected countries
    return cities.filter((city) =>
      constraints.countries?.some((c) => c.code === city.countryCode),
    );
  }, [constraints.countries, citiesList]);

  // const filters = useMemo(() => {
  //   if (!selectedDynamicPage?.data().connections) return;

  //   return Object.values(selectedDynamicPage?.data().connections ?? {}).map(
  //     (c) => ({
  //       ...(c.type === 'adsCampaign' ? { campaignId: c.id } : {}),
  //       adsAccountId: c.adsAccountId,
  //       provider: c.provider,
  //     }),
  //   );
  // }, [selectedDynamicPage]);

  const constraintsChildren = [];

  if (showKeywords) {
    constraintsChildren.push(
      <ConstraintsTypeSection
        type="keywords"
        title="Keywords"
        tooltip={({ keyword }) => `Keyword: ${keyword}`}
        chipTitle={(item) => item.keyword}
        emptyMessage="No keywords selected"
        constraints={constraints}
        onChange={onChange}
        // filters={filters}
        selectButton={
          <Button
            className="h-full"
            variant="ghost"
            color="primary"
            size="sm"
            onClick={() => {
              setSelectKeywordsModalOpen(true);
            }}
          >
            Select
          </Button>
        }
      />,
    );
  }

  if (showDevices) {
    constraintsChildren.push(
      <div className="mb-2 flex w-full flex-col gap-2">
        <p className="text-sm font-medium text-gray-900">Devices</p>
        <ToggleGroup
          type="multiple"
          variant="outline"
          disabled={readOnly}
          value={!constraints.devices?.length ? ['all'] : constraints.devices}
          onValueChange={(newDevices: (DeviceConstraint | 'all')[]) => {
            if (constraints.devices?.length && newDevices.includes('all')) {
              onChange({ ...constraints, devices: [] });
            } else {
              onChange({
                ...constraints,
                devices: newDevices
                  .filter((x) => x !== 'all')
                  .map((x) => x satisfies DeviceConstraint),
              });
            }
          }}
        >
          <ToggleGroupItem value="all">All Devices</ToggleGroupItem>
          <ToggleGroupItem value="mobile">
            <DevicePhoneMobileIcon className="h-5 w-5" />
            Mobile
          </ToggleGroupItem>
          <ToggleGroupItem value="tablet">
            <DeviceTabletIcon className="h-5 w-5" />
            Tablet
          </ToggleGroupItem>
          <ToggleGroupItem value="desktop">
            <ComputerDesktopIcon className="h-5 w-5" />
            Desktop
          </ToggleGroupItem>
        </ToggleGroup>
      </div>,
    );
  }

  if (showSchedules) {
    constraintsChildren.push(
      <div className="mb-2 flex w-full flex-col gap-4">
        <div className="flex flex-row items-end justify-between">
          <p className="text-sm font-medium text-gray-900">Schedule</p>
          <Button
            variant="ghost"
            size="micro"
            color={constraints.schedules?.length ? 'negative' : 'primary'}
            onClick={() => {
              if (constraints.schedules?.length) {
                onChange({
                  ...constraints,
                  schedules: [],
                });
                return;
              }
              const newSchedule = {
                id: uid(),
                timeZone: 'UTC',
              };

              onChange({
                ...constraints,
                schedules: [newSchedule],
              });
            }}
            disabled={readOnly}
          >
            {constraints.schedules?.length ? 'Remove Schedule' : 'Add Schedule'}
          </Button>
        </div>
        {constraints.schedules?.length ? (
          <div className="flex flex-col gap-4">
            <Autocomplete
              selected={constraints.schedules?.[0]?.timeZone ?? 'UTC'}
              onChange={(e) => {
                const newSchedule = {
                  id: uid(),
                  ...constraints.schedules?.[0],
                  timeZone: e || 'UTC',
                };

                onChange({
                  ...constraints,
                  schedules: [newSchedule],
                });
              }}
              options={timezonesList}
              modal
            />
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
              <div className="flex-1">
                <Input
                  type="date"
                  title="From Date (optional)"
                  value={constraints.schedules?.[0]?.startDate || ''}
                  onChange={(v) => {
                    const newSchedule = {
                      id: uid(),
                      timeZone: 'UTC',
                      ...constraints.schedules?.[0],
                      startDate: v.target.value?.split('T')[0] || (null as any),
                    };

                    onChange({
                      ...constraints,
                      schedules: [newSchedule],
                    });
                  }}
                />
              </div>
              <div className="flex-1">
                <Input
                  type="time"
                  title="From Time"
                  value={constraints.schedules?.[0]?.startTime ?? '00:00'}
                  onChange={(v) => {
                    const newSchedule = {
                      id: uid(),
                      timeZone: 'UTC',
                      ...constraints.schedules?.[0],
                      startTime: v.target.value,
                    };

                    onChange({
                      ...constraints,
                      schedules: [newSchedule],
                    });
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
              <div className="flex-1">
                <Input
                  type="date"
                  title="To Date (optional)"
                  value={constraints.schedules?.[0]?.endDate ?? undefined}
                  onChange={(v) => {
                    const newSchedule = {
                      id: uid(),
                      timeZone: 'UTC',
                      ...constraints.schedules?.[0],
                      endDate: v.target.value?.split('T')[0] || (null as any),
                    };

                    onChange({
                      ...constraints,
                      schedules: [newSchedule],
                    });
                  }}
                />
              </div>
              <div className="flex-1">
                <Input
                  type="time"
                  title="To Time"
                  value={constraints.schedules?.[0]?.endTime ?? '23:59'}
                  onChange={(v) => {
                    const newSchedule = {
                      id: uid(),
                      timeZone: 'UTC',
                      ...constraints.schedules?.[0],
                      endTime: v.target.value,
                    };

                    onChange({
                      ...constraints,
                      schedules: [newSchedule],
                    });
                  }}
                />
              </div>
            </div>
            <ChipsAutocomplete
              title="Days of Week (optional)"
              selected={constraints.schedules?.[0]?.daysOfWeek ?? []}
              onChange={(days) => {
                const newSchedule = {
                  id: uid(),
                  timeZone: 'UTC',
                  ...constraints.schedules?.[0],
                  daysOfWeek: days.length
                    ? days?.sort(
                        (a, b) =>
                          daysOfWeek.findIndex((d) => d.value === a) -
                          daysOfWeek.findIndex((d) => d.value === b),
                      )
                    : (null as any),
                };

                onChange({
                  ...constraints,
                  schedules: [newSchedule],
                });
              }}
              options={daysOfWeek.map((d) => d.value)}
              getValue={(o) => {
                return daysOfWeek.find((d) => d.value === o)?.title || o;
              }}
              disabled={readOnly}
              modal
            />
            <ChipsAutocomplete
              title="Hours of Day (optional)"
              selected={constraints.schedules?.[0]?.hoursOfDay ?? []}
              onChange={(hours) => {
                const newSchedule = {
                  id: uid(),
                  timeZone: 'UTC',
                  ...constraints.schedules?.[0],
                  hoursOfDay: hours.length
                    ? hours.sort((a, b) => a - b)
                    : (null as any),
                };

                onChange({
                  ...constraints,
                  schedules: [newSchedule],
                });
              }}
              options={hoursOfDay.map((h) => h.value)}
              getValue={(o) => {
                return hoursOfDay.find((h) => h.value === o)?.title || '';
              }}
              disabled={readOnly}
              modal
            />
          </div>
        ) : (
          <div className="flex h-full w-full items-center">
            <p className="text-sm text-gray-500">No schedule set</p>
          </div>
        )}
      </div>,
    );
  }

  if (showAds) {
    constraintsChildren.push(
      <ConstraintsAdsAccountItemsSection
        type="ads"
        title="Ads"
        tooltip={(item) => {
          if (item.provider === 'facebook' || item.provider === 'google') {
            return <AdPreview ad={item} />;
          }
          return `Ad Id: ${(item as any).id}`;
        }}
        emptyMessage="No ads selected"
        constraints={constraints}
        onChange={onChange}
        // filters={filters}
      />,
    );
  }

  if (showAdsGroup) {
    constraintsChildren.push(
      <ConstraintsAdsAccountItemsSection
        type="adGroups"
        title="Ad Groups"
        tooltip={({ id }) => `Ad Group Id: ${id}`}
        emptyMessage="No ad groups selected"
        constraints={constraints}
        onChange={onChange}
        // filters={filters}
      />,
    );
  }

  if (showCampaigns) {
    constraintsChildren.push(
      <ConstraintsAdsAccountItemsSection
        type="campaigns"
        title="Campaigns"
        tooltip={({ id }) => `Campaign Id: ${id}`}
        emptyMessage="No campaigns selected"
        constraints={constraints}
        onChange={onChange}
        // filters={filters}
      />,
    );
  }

  if (showCountries) {
    constraintsChildren.push(
      <div className="mb-2 flex w-full flex-col gap-2">
        <p className="text-sm font-medium text-gray-900">Countries</p>
        <ChipsAutocomplete
          placeholder="Select countries..."
          options={countryCodes}
          selected={countryCodes.filter((c) =>
            constraints.countries?.some((cc) => cc.code === c.country),
          )}
          onChange={(selected) => {
            onChange({
              ...constraints,
              countries: selected.map((c) => ({
                id: uid(),
                code: c.country,
                name: c.name,
              })),
            });
          }}
          getOptionId={(option) => option.country}
          getValue={(option) => `${option.name} ${option.country}`}
          renderOption={(option) => (
            <div className="flex items-center gap-2">
              <span>{option.flag}</span>
              <span>{option.name}</span>
              <span className="text-xs text-gray-500">{option.country}</span>
            </div>
          )}
          renderItem={(option) => (
            <div className="flex items-center gap-1">
              <span>{option.flag}</span>
              <span>{option.name}</span>
            </div>
          )}
          modal
        />
      </div>,
    );
  }

  if (showRegions) {
    constraintsChildren.push(
      <div className="mb-2 flex w-full flex-col gap-2">
        <p className="text-sm font-medium text-gray-900">Regions / States</p>
        {availableRegions.length === 0 ? (
          <p className="text-sm text-gray-500">
            {Object.keys(regionsByCountry).length === 0
              ? 'Loading regions...'
              : 'No regions available for selected countries'}
          </p>
        ) : (
          <ChipsAutocomplete
            placeholder="Select regions..."
            options={availableRegions}
            selected={availableRegions.filter((r) =>
              constraints.regions?.some(
                (cr) => cr.code === r.code && cr.countryCode === r.countryCode,
              ),
            )}
            onChange={(selected) => {
              onChange({
                ...constraints,
                regions: selected.map((r) => ({
                  id: uid(),
                  code: r.code,
                  name: r.name,
                  countryCode: r.countryCode,
                })),
              });
            }}
            getOptionId={(option) => `${option.countryCode}-${option.code}`}
            getValue={(option) => `${option.name} (${option.countryCode})`}
            renderOption={(option) => (
              <div className="flex items-center gap-2">
                <span>{option.name}</span>
                <span className="text-xs text-gray-500">
                  {option.countryCode}-{option.code}
                </span>
              </div>
            )}
            renderItem={(option) => (
              <div className="flex items-center gap-1">
                <span>{option.name}</span>
                <span className="text-xs text-gray-400">
                  ({option.countryCode})
                </span>
              </div>
            )}
            modal
          />
        )}
      </div>,
    );
  }

  if (showCities) {
    // Include custom cities (cities in constraints that aren't in the static list)
    const customCities =
      constraints.cities
        ?.filter((c) => !availableCities.some((ac) => ac.name === c.name))
        .map((c) => ({
          id: c.id,
          name: c.name,
          displayName: c.displayName,
          countryCode: c.countryCode,
          regionCode: c.regionCode,
          isCustom: true as const,
        })) || [];

    const allCityOptions = [
      ...availableCities.map((c) => ({ ...c, isCustom: false as const })),
      ...customCities,
    ];

    // Custom filter that includes a "create" option for free text
    const cityFilter = (
      query: string,
      options: typeof allCityOptions,
    ): typeof allCityOptions => {
      const normalizedQuery = normalizeLocationString(query);
      const filtered = options.filter((opt) =>
        opt.displayName.toLowerCase().includes(query.toLowerCase()),
      );

      // If query doesn't exactly match any existing option, add a "create" option
      const exactMatch = options.some(
        (opt) => normalizeLocationString(opt.displayName) === normalizedQuery,
      );

      if (query.trim() && !exactMatch) {
        filtered.push({
          id: `custom-${query}`,
          name: normalizeLocationString(query),
          displayName: query.trim(),
          countryCode: undefined,
          regionCode: undefined,
          isCustom: true,
        });
      }

      return filtered;
    };

    constraintsChildren.push(
      <div className="mb-2 flex w-full flex-col gap-2">
        <p className="text-sm font-medium text-gray-900">Cities</p>
        <ChipsAutocomplete
          placeholder="Search or type a city name..."
          options={allCityOptions}
          selected={allCityOptions.filter((c) =>
            constraints.cities?.some((cc) => cc.name === c.name),
          )}
          onChange={(selected) => {
            onChange({
              ...constraints,
              cities: selected.map((c) => ({
                id: c.id.startsWith('custom-') ? uid() : c.id,
                name: c.name,
                displayName: c.displayName,
                countryCode: c.countryCode,
                regionCode: c.regionCode,
              })),
            });
          }}
          filter={cityFilter}
          getOptionId={(c) => c.id}
          getValue={(c) => c.displayName}
          renderOption={(c) => (
            <div className="flex items-center gap-2">
              {c.id.startsWith('custom-') ? (
                <span className="text-brand-600">
                  Add &ldquo;{c.displayName}&rdquo;
                </span>
              ) : (
                <span>{c.displayName}</span>
              )}
            </div>
          )}
          renderItem={(c) => <span>{c.displayName}</span>}
          disabled={readOnly}
          modal
        />
      </div>,
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <KeywordsConstraintSelectorModal
        constraints={constraints}
        open={selectKeywordsModalOpen}
        onClose={() => {
          setSelectKeywordsModalOpen(false);
        }}
        onSave={onChange}
      />
      {constraintsChildren
        .flatMap((item, index, arr) => [
          item,
          index < arr.length - 1 ? (
            <div className="flex items-center gap-2">
              <div className="h-px grow bg-gray-200"></div>
              <div>
                <p className="text-xs font-medium text-gray-900 italic">and</p>
              </div>
              <div className="h-px grow bg-gray-200"></div>
            </div>
          ) : null,
        ])
        .filter(Boolean)}
    </div>
  );
}

const MAX_CHIPS = 10;

interface ConstraintsTypeSectionProps<T extends ConstraintType> {
  type: T;
  title: string;
  emptyMessage: string;
  tooltip:
    | string
    | ((item: NonNullable<Constraints[T]>[0]) => string | ReactNode);
  constraints: Constraints;
  chipTitle: (item: NonNullable<Constraints[T]>[0]) => string;
  onChange: (_: Constraints) => void;
  selectButton: ReactNode;
}

const ConstraintsTypeSection = <
  T extends Extract<
    ConstraintType,
    'campaigns' | 'adGroups' | 'ads' | 'keywords'
  >,
>({
  type,
  title,
  emptyMessage,
  tooltip,
  constraints,
  chipTitle,
  onChange,
  selectButton,
}: ConstraintsTypeSectionProps<T>) => {
  const selected = constraints[type];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex w-full items-center justify-end gap-2">
        <div className="grow text-sm font-medium text-gray-900">{title}</div>
        {selectButton}
      </div>
      <div className="flex flex-1 flex-wrap gap-2">
        {!selected?.length && (
          <p className="text-sm text-gray-500">{emptyMessage}</p>
        )}
        {selected?.slice(0, MAX_CHIPS).map((item) => (
          <Tooltip key={item.id}>
            <TooltipTrigger>
              <Chip
                label={chipTitle(item)}
                icon={
                  item?.provider === 'facebook' ? (
                    <img src={facebookAdsIcon} className="size-3" />
                  ) : (
                    <img src={googleAdsIcon} className="size-3" />
                  )
                }
                onDelete={() => {
                  onChange({
                    ...constraints,
                    [type]: selected?.filter((_item) => _item.id !== item.id),
                  });
                }}
              />
            </TooltipTrigger>
            <TooltipContent variant="white">
              {typeof tooltip === 'string' ? tooltip : tooltip(item)}
            </TooltipContent>
          </Tooltip>
        ))}
        {(selected?.length ?? 0) > MAX_CHIPS && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="micro" color="primary">
                Show More
              </Button>
            </PopoverTrigger>

            <PopoverContent
              side="top"
              align="end"
              className="z-[9999] h-80 w-96"
            >
              <div className="flex h-full flex-col divide-y divide-gray-200">
                <div className="pb-2 font-medium">Selected {title}</div>
                <div className="flex flex-1 flex-col justify-stretch gap-2 overflow-scroll py-2">
                  {selected?.map((item) => (
                    <Button
                      key={item.id}
                      variant="outline"
                      size="default"
                      className="relative inline-block justify-start ps-4 pe-9 text-start"
                    >
                      <span>
                        {capitalize(
                          ('name' in item && item.name) ||
                            ('keyword' in item && item.keyword) ||
                            ('headlines' in item &&
                              item.headlines
                                .sort(
                                  (h1: any, h2: any) =>
                                    h1.metrics.impressions -
                                    h2.metrics.impressions,
                                )
                                .map((h: any) => h.text)
                                ?.slice(0, 3)
                                .join(' | ')) ||
                            'Ad',
                        )}
                      </span>{' '}
                      <span className="text-xs text-gray-400">- {item.id}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
};

type ConstraintsAdsAccountItemsSectionProps<
  T extends Extract<ConstraintType, 'campaigns' | 'adGroups' | 'ads'>,
> = Omit<ConstraintsTypeSectionProps<T>, 'selectButton' | 'chipTitle'> & {
  filters?: {
    campaignId?: string;
    adsAccountId: string;
    provider: 'google' | 'facebook';
  }[];
};

const ConstraintsAdsAccountItemsSection = <
  T extends Extract<ConstraintType, 'campaigns' | 'adGroups' | 'ads'>,
>({
  type,
  title,
  emptyMessage,
  tooltip,
  constraints,
  onChange,
  filters,
}: ConstraintsAdsAccountItemsSectionProps<T>) => {
  return (
    <ConstraintsTypeSection
      type={type}
      title={title}
      emptyMessage={emptyMessage}
      tooltip={tooltip}
      chipTitle={(item) => getConstraintTitle(type, item)}
      constraints={constraints}
      onChange={onChange}
      selectButton={
        <AdsAccountsDataConstraintPopover
          type={type}
          selected={(constraints[type] || []) as any[]}
          onChange={(items: any) => {
            onChange({
              ...constraints,
              [type]: items,
            });
          }}
          filters={filters}
        />
      }
    />
  );
};

type AdsItemsConstraintSelectorCampaigns = {
  type: Extract<ConstraintType, 'campaigns'>;
  selected: AdsCampaignConstraint[];
  onChange: (_: AdsCampaignConstraint[]) => void;

  filters?: {
    campaignId?: string;
    adsAccountId: string;
    provider: 'google' | 'facebook';
  }[];
};

type AdsItemsConstraintSelectorAdGroups = {
  type: Extract<ConstraintType, 'adGroups'>;
  selected: AdGroupConstraint[];
  onChange: (_: AdGroupConstraint[]) => void;

  filters?: {
    campaignId?: string;
    adsAccountId: string;
    provider: 'google' | 'facebook';
  }[];
};

type AdsItemsConstraintSelectorAds = {
  type: Extract<ConstraintType, 'ads'>;
  selected: AdConstraint[];
  onChange: (_: AdConstraint[]) => void;

  filters?: {
    campaignId?: string;
    adsAccountId: string;
    provider: 'google' | 'facebook';
  }[];
};

type AdsAccountsDataConstraintSelectorProps =
  | AdsItemsConstraintSelectorCampaigns
  | AdsItemsConstraintSelectorAdGroups
  | AdsItemsConstraintSelectorAds;

const AdsAccountsDataConstraintPopover = (
  props: AdsAccountsDataConstraintSelectorProps,
) => {
  return (
    <Popover modal>
      <PopoverTrigger asChild>
        <Button className="h-full" variant="ghost" color="primary" size="sm">
          Select
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side="top"
        align="end"
        className="z-[9999] h-80 w-96 py-2.5 pb-0"
      >
        <AdsAccountsDataConstraint {...props} />
      </PopoverContent>
    </Popover>
  );
};

const AdsAccountsDataConstraint = ({
  type,
  selected,
  onChange,
  filters,
}: AdsAccountsDataConstraintSelectorProps) => {
  const { selectedAccount } = useContext(UserContext);

  const [campaigns, setCampaigns] = useState<AdsCampaign[]>();
  const [campaignAdGroups, setCampaignAdGroups] = useState<AdsGroup[]>();
  const [adGroupAds, setAdGroupAds] = useState<AdsGroupAd[]>();

  const [currentCampaign, setCurrentCampaign] = useState<AdsCampaign>();
  const [currentAdGroup, setCurrentAdGroup] = useState<AdsGroup>();
  const [error, setError] = useState<string>();

  const [showSearch, setShowSearch] = useState(false);
  const [searchQueries, setSearchQueries] = useState<
    Record<typeof type, string>
  >({} as any);

  const reset = useCallback(() => {
    setCurrentCampaign(undefined);
    setCurrentAdGroup(undefined);
    setCampaigns(undefined);
    setCampaignAdGroups(undefined);
    setAdGroupAds(undefined);
    setError(undefined);
  }, []);

  useEffect(() => {
    reset();
  }, [type, filters, reset]);

  useEffect(() => {
    setCampaigns(undefined);
    setError(undefined);

    if (!selectedAccount) return;

    getCachedCampaigns(
      selectedAccount.id,
      distinct(filters?.map((f) => f.provider) ?? ['google']),
      filters,
    )
      .then((data) => {
        setCampaigns(data);
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError('Failed to fetch campaigns');
      });
  }, [filters, selectedAccount]);

  useEffect(() => {
    setCampaignAdGroups(undefined);
    setError(undefined);

    if (!selectedAccount || !currentCampaign) return;

    getCachedAdGroups(
      selectedAccount.id,
      currentCampaign.provider,
      currentCampaign.adsAccountId,
      [currentCampaign.id],
    )
      .then((data) => {
        setCampaignAdGroups(data);
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError('Failed to fetch ad groups');
      });
  }, [currentCampaign, selectedAccount]);

  useEffect(() => {
    setAdGroupAds(undefined);
    setError(undefined);

    if (!selectedAccount || !currentAdGroup || !currentCampaign) return;

    getCachedAdGroupAds(
      selectedAccount.id,
      currentAdGroup.provider,
      currentAdGroup.adsAccountId,
      [currentCampaign.id],
      [currentAdGroup.id],
    )
      .then((data) => {
        setAdGroupAds(data);
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError('Failed to fetch ads');
      });
  }, [currentCampaign, currentAdGroup, selectedAccount]);

  const currentLevel: 'campaigns' | 'adGroups' | 'ads' = useMemo(() => {
    return currentAdGroup ? 'ads' : currentCampaign ? 'adGroups' : 'campaigns';
  }, [currentCampaign, currentAdGroup]);

  const searchQuery = useMemo(
    () => searchQueries[currentLevel],
    [searchQueries, currentLevel],
  );

  const currentList = useMemo(() => {
    switch (currentLevel) {
      case 'campaigns':
        return campaigns?.filter((c) =>
          c.name.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
        );
      case 'adGroups':
        return campaignAdGroups?.filter((c) =>
          c.name.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
        );
      case 'ads':
        return adGroupAds?.filter(
          (
            c: Pick<Partial<GoogleAdsAdGroupAd>, 'headlines' | 'descriptions'> &
              Pick<Partial<FacebookAdsAdSetAd>, 'titles' | 'bodies'>,
          ) =>
            c.headlines?.some((h: any) =>
              h.text.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
            ) ||
            c.descriptions?.some((d: any) =>
              d.text.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
            ) ||
            c.titles?.some((t: any) =>
              t.text.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
            ) ||
            c.bodies?.some((b: any) =>
              b.text.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
            ),
        );
    }
  }, [currentLevel, campaigns, campaignAdGroups, adGroupAds, searchQuery]);

  return (
    <div className="relative flex h-full w-full flex-col">
      <div
        className={classNames(
          'border-b border-b-gray-200 px-3',
          showSearch && 'pb-2',
          'w-full',
        )}
      >
        <div className="flex w-full justify-between gap-2">
          <Breadcrumbs
            mode="fit"
            className="flex-1 shrink overflow-hidden"
            breadcrumbs={[
              ...(currentLevel === 'campaigns'
                ? [
                    {
                      id: 'campaigns',
                      name: 'Campaigns',
                      href: '',
                      current: true,
                    },
                  ]
                : []),

              ...(currentLevel === 'adGroups'
                ? [
                    {
                      id: 'campaigns',
                      name: (
                        <div className="flex items-center gap-2">
                          {currentCampaign?.provider === 'facebook' ? (
                            <img src={facebookAdsIcon} className="h-4 w-4" />
                          ) : (
                            <img src={googleAdsIcon} className="h-4 w-4" />
                          )}
                          <span className="truncate">
                            {currentCampaign?.name ?? ''}
                          </span>
                        </div>
                      ),
                      href: '',
                      onClick: () => setCurrentCampaign(undefined),
                    },
                    {
                      id: 'adGroups',
                      name: 'Ad Groups',
                      href: '',
                      current: true,
                    },
                  ]
                : []),

              ...(currentLevel === 'ads'
                ? [
                    {
                      id: 'campaigns',
                      name: (
                        <div className="flex items-center gap-2">
                          {currentCampaign?.provider === 'facebook' ? (
                            <img src={facebookAdsIcon} className="h-4 w-4" />
                          ) : (
                            <img src={googleAdsIcon} className="h-4 w-4" />
                          )}
                          <span className="truncate">
                            {currentCampaign?.name ?? ''}
                          </span>
                        </div>
                      ),
                      href: '',
                      onClick: () => {
                        setCurrentAdGroup(undefined);
                        setCurrentCampaign(undefined);
                      },
                    },
                    {
                      id: 'adGroups',
                      name: currentAdGroup?.name ?? '',
                      href: '',
                      onClick: () => setCurrentAdGroup(undefined),
                    },
                    { id: 'ads', name: 'Ads', href: '', current: true },
                  ]
                : []),
            ]}
          />

          <Button
            className="relative"
            variant="ghost"
            size="icon"
            onClick={() => {
              setShowSearch((x) => !x);
            }}
          >
            <MagnifyingGlassIcon className="size-4 text-gray-600" />
          </Button>
        </div>

        {showSearch && (
          <SearchField
            value={searchQuery || ''}
            onChange={(e) => {
              setSearchQueries((d) => ({
                ...d,
                [currentLevel]: e.target.value,
              }));
            }}
          />
        )}
      </div>

      <div className="flex h-full w-full flex-col gap-1 overflow-y-scroll p-3">
        {!currentList && !error && (
          <div className="flex h-full w-full flex-col items-center justify-center">
            <Loader />
          </div>
        )}
        {!currentList && !!error && (
          <div className="flex h-full w-full flex-col items-center justify-center">
            <p className="text-center text-red-500">{error}</p>
          </div>
        )}
        {currentList?.length === 0 && <p>No items found</p>}
        {currentList?.map((item) => {
          const itemSeleted = selected.some((s) => s.id === item.id);
          return (
            <Button
              key={item.id}
              variant={itemSeleted ? 'default' : 'outline'}
              size="default"
              onClick={() => {
                if (currentLevel === type) {
                  onChange(
                    itemSeleted
                      ? selected.filter((t) => t.id !== item.id)
                      : ([...selected, item] as any),
                  );
                } else if (currentLevel === 'campaigns') {
                  setCurrentCampaign(item as AdsCampaign);
                } else if (currentLevel === 'adGroups') {
                  setCurrentAdGroup(item as AdsGroup);
                }
              }}
              className="relative w-full max-w-full justify-start ps-4 pe-9 text-center"
            >
              {currentLevel === 'campaigns' && (
                <>
                  {item.provider === 'facebook' ? (
                    <img src={facebookAdsIcon} className="size-3" />
                  ) : (
                    <img src={googleAdsIcon} className="size-3" />
                  )}
                </>
              )}

              <span className="flex-1 truncate text-start">
                {capitalize(
                  getConstraintTitle(currentLevel, item),
                  // ('name' in item && item.name) ||
                  //   ('headlines' in item &&
                  //     item.headlines
                  //       .sort(
                  //         (h1: any, h2: any) =>
                  //           h1.metrics.impressions - h2.metrics.impressions,
                  //       )
                  //       .map((h: any) => h.text)
                  //       ?.slice(0, 3)
                  //       .join(' | ')) ||
                  //   'Ad',
                )}
              </span>

              {currentLevel === type ? (
                <CheckIcon
                  className={classNames(
                    'h-4 w-4',
                    itemSeleted ? 'visible' : 'invisible',
                  )}
                />
              ) : (
                <ChevronRightIcon
                  className={classNames('h-4 w-4 text-gray-400')}
                />
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
